import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Spinner } from '../components/ui';
import { Eye, EyeOff, ArrowRight, ShieldCheck, Shirt, Scissors, TrendingUp } from 'lucide-react';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro('');
    setCarregando(true);
    try {
      await login(email, senha);
      navigate('/');
    } catch (err: any) {
      setErro(err.response?.data?.error || 'Erro ao fazer login. Verifique suas credenciais.');
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Painel de marca */}
      <div className="hidden lg:flex lg:w-[46%] xl:w-1/2 relative flex-col justify-between p-12 bg-navy-950 overflow-hidden">
        {/* Brilhos decorativos */}
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-brand-600/25 blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[28rem] h-[28rem] rounded-full bg-brand-500/15 blur-[140px]" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)',
            backgroundSize: '44px 44px',
          }}
        />

        <div className="relative">
          <img
            src="/brand/logo-monchery-branca.png"
            alt="Monchery Malharia"
            className="h-16 w-auto"
            draggable={false}
          />
        </div>

        <div className="relative space-y-8">
          <h1 className="font-display text-4xl xl:text-[2.75rem] font-bold text-white leading-[1.15] tracking-tight">
            A operação da sua malharia,
            <span className="text-brand-400"> em um só lugar.</span>
          </h1>
          <div className="space-y-5">
            {[
              { icon: Shirt, texto: 'Pedidos e produção com prazos sob controle' },
              { icon: Scissors, texto: 'Lotes de costura e pagamentos de costureiras' },
              { icon: TrendingUp, texto: 'Financeiro do dia a dia, sem planilhas' },
            ].map(({ icon: Icon, texto }) => (
              <div key={texto} className="flex items-center gap-3.5">
                <div className="w-9 h-9 rounded-lg bg-white/[0.07] border border-white/[0.08] flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-brand-300" />
                </div>
                <p className="text-[15px] text-ink-300">{texto}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="relative text-2xs text-ink-500 tracking-wide">
          © {new Date().getFullYear()} Monchery — Sistema interno de gestão
        </p>
      </div>

      {/* Formulário */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-[400px] animate-fade-up">
          {/* Logo mobile */}
          <div className="lg:hidden mb-10">
            <img
              src="/brand/logo-monchery.png"
              alt="Monchery Malharia"
              className="h-16 w-auto"
              draggable={false}
            />
          </div>

          <h2 className="font-display text-2xl font-bold text-ink-950 tracking-tight">Bem-vindo de volta</h2>
          <p className="text-sm text-ink-500 mt-2">Acesse sua conta para continuar.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label className="input-label" htmlFor="email">E-mail</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="voce@monchery.com"
                className="input-field"
                autoComplete="email"
                required
              />
            </div>

            <div>
              <label className="input-label" htmlFor="senha">Senha</label>
              <div className="relative">
                <input
                  id="senha"
                  type={mostrarSenha ? 'text' : 'password'}
                  value={senha}
                  onChange={e => setSenha(e.target.value)}
                  placeholder="••••••••"
                  className="input-field pr-11"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setMostrarSenha(!mostrarSenha)}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 icon-btn"
                  aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {mostrarSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {erro && (
              <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-[13px] font-medium text-red-700 animate-fade-in">
                {erro}
              </div>
            )}

            <button
              type="submit"
              disabled={carregando}
              className="btn-primary w-full !py-3 text-[15px]"
            >
              {carregando ? (
                <Spinner className="h-5 w-5 !border-white/30 !border-t-white" />
              ) : (
                <>
                  Entrar
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <div className="mt-10 flex items-center gap-2.5 text-2xs text-ink-400">
            <ShieldCheck className="w-4 h-4 text-ink-300" />
            Acesso restrito à equipe Monchery. Suas sessões são protegidas.
          </div>
        </div>
      </div>
    </div>
  );
}
